import { h, defineComponent, ref } from 'vue';
import { IssueReportExport } from '@/ui/pages/issue/export/IssueReportExport';
import {
  issuesReportExportUrl,
  SupportedExportFormat,
  SupportedExportFormatEnum,
} from '@/services/api/resources/issue/exports';
import { flow } from 'fp-ts/es6/function';
import { useTask } from '@/ui/concerns';
import { user } from '@/ui/auth/auth';
import { useIssueExport } from '@/ui/pages/issue/useIssueExport';
import { IssueQuery } from '@/services/api/resources/issue/v2/query';

interface IssueIndexExportProps {
  query: IssueQuery;
  formats: Array<SupportedExportFormatEnum>;
}

export const IssueIndexExport = defineComponent<IssueIndexExportProps>({
  name: 'IssueIndexExport',
  props: ['selectedResources', 'formats', 'query'],
  setup(props, context) {
    const loading = ref(false);

    const { saveFileFromUrl } = useIssueExport();

    const runLoadingIndicator = () => {
      loading.value = true;
      setTimeout(() => {
        loading.value = false;
      }, 3000);
    };

    const exportReports = (format: SupportedExportFormat) => {
      const { run } = useTask(
        () => issuesReportExportUrl(
          props.query,
          format,
          user.value.timezone,
        ),
        url => {
          saveFileFromUrl(url.href);
          context.emit('exported');
        },
      );

      return run;
    };

    const exportReportWithLoading = (format: SupportedExportFormat) => flow(
      runLoadingIndicator,
      exportReports(format),
    );

    const onExportClick = ({ format }: {format: SupportedExportFormat | 'photos'}) => {
      loading.value = true;
      exportReportWithLoading(format as SupportedExportFormat)();
    };

    return function render() {
      return h(IssueReportExport, {
        props: {
          loading: loading.value,
          iconized: true,
          tooltiped: true,
          formats: props.formats,
        },
        on: {
          export: onExportClick,
        },
      });
    };
  },
});

export const issueIndexExport = (
  { props, on }: { props: IssueIndexExportProps; on?: Record<string, Function> },
) => h(IssueIndexExport, { props, on });
